import axios from "axios";
import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  PASSWORD_REQUIREMENTS_TOOLTIP_HTML,
  PASSWORD_REQUIREMENTS_TOOLTIP_ID,
} from "../../constants/passwordTooltip";
import { SitesContext } from "../../contexts/sitesContext";
import { toast } from "react-toastify";
import { GoogleLogin } from "@react-oauth/google";
import TextField from "@mui/material/TextField";
import ReCaptcha from "../recaptcha";

import styles from "./register.module.css";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const Register = () => {
  const navigate = useNavigate();
  const { setJwtToken, setUserId, setIsSubscribed, themeObj, theme } =
    useContext(SitesContext);

  const filledInputProps = { disableUnderline: true };

  const [firstName, setFirstName] = useState("");
  const [firstNameError, setFirstNameError] = useState("");

  const [lastName, setLastName] = useState("");
  const [lastNameError, setLastNameError] = useState("");

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");

  const [password, setPassword] = useState("");
  const [isPasswordShowing, setIsPasswordShowing] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const [passwordAgain, setPasswordAgain] = useState("");
  const [isPasswordAgainShowing, setIsPasswordAgainShowing] = useState(false);
  const [passwordAgainError, setPasswordAgainError] = useState("");

  const [captchaToken, setCaptchaToken] = useState("");
  // Honeypot — see .honeypot in register.module.css. Always empty for humans.
  const [website, setWebsite] = useState("");

  const captchaRef = useRef(null);

  const resetCaptcha = () => {
    captchaRef.current?.reset();
    setCaptchaToken("");
  };

  const PASSWORD_REGEX =
    "^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{12,}$";
  const PASSWORD_ERROR = "This password does not meet the requirements.";
  const REQUIRED_FIELD_ERROR = "This field is required.";

  useEffect(() => {
    document.body.style.backgroundImage = themeObj.landingBackground;
  }, [theme]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      email &&
        axios
          .get(
            `${process.env.REACT_APP_API_BASE}/api/users/register/check-email/${email}`
          )
          .then(() => setEmailError(""))
          .catch(() => setEmailError("This email is already in use."));
    }, 2000);

    return () => clearTimeout(delayDebounceFn);
  }, [email]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      username &&
        axios
          .get(
            `${process.env.REACT_APP_API_BASE}/api/users/register/check-username/${username}`
          )
          .then(() => setUsernameError(""))
          .catch(() =>
            setUsernameError(
              "That username is already in use. Please try another one."
            )
          );
    }, 2000);

    return () => clearTimeout(delayDebounceFn);
  }, [username]);

  useEffect(() => {
    if (password && !password.match(PASSWORD_REGEX)) {
      setPasswordError(PASSWORD_ERROR);
    } else if (passwordAgain && !passwordAgain.match(PASSWORD_REGEX)) {
      setPasswordAgainError(PASSWORD_ERROR);
    } else if (password && passwordAgain && !(password === passwordAgain)) {
      setPasswordAgainError("These passwords don't match.");
    } else {
      setPasswordError("");
      setPasswordAgainError("");
    }
  }, [password, passwordAgain]);

  const onSubmit = (e) => {
    e.preventDefault();

    const isFilledOut =
      firstName && lastName && email && username && password && passwordAgain;
    const hasError =
      usernameError || emailError || passwordError || passwordAgainError;

    if (isFilledOut && !hasError) {
      if (RECAPTCHA_SITE_KEY && !captchaToken) {
        toast("Please confirm you are not a robot.", {
          type: "error",
          theme,
        });
        return;
      }

      axios
        .post(
          `${process.env.REACT_APP_API_BASE}/api/users/register`,
          {
            firstName,
            lastName,
            email,
            username,
            password,
            recaptchaToken: captchaToken,
            website,
          },
          { withCredentials: true }
        )
        .then((res) => {
          setUserId(res.data.id);
          navigate("/account/please-verify");
          toast("Registered successfully. Please verify your email.", {
            type: "success",
            theme,
          });
        })
        .catch((err) => {
          const serverError = err.response?.data?.error;
          // The server distinguishes a failed captcha from a taken username;
          // anything else falls back to the username message it used before.
          toast(
            serverError && serverError.startsWith("Captcha")
              ? serverError
              : "A user with that username already exists. Please log in.",
            { type: "error", theme }
          );
          // The token is single-use once the server checks it, so make the
          // user tick the box again before retrying.
          resetCaptcha();
        });
    } else {
      if (!firstName) {
        setFirstNameError(REQUIRED_FIELD_ERROR);
      }
      if (!lastName) {
        setLastNameError(REQUIRED_FIELD_ERROR);
      }
      if (!email) {
        setEmailError(REQUIRED_FIELD_ERROR);
      }
      if (!username) {
        setUsernameError(REQUIRED_FIELD_ERROR);
      }
      if (!password) {
        setPasswordError(REQUIRED_FIELD_ERROR);
      }
      if (!passwordAgain) {
        setPasswordAgainError(REQUIRED_FIELD_ERROR);
      }
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      onSubmit(e);
    }
  };

  // Google hands us a signed ID token. Send only that — the API reads the
  // name and email out of it, so nothing here is worth tampering with.
  const responseGoogle = ({ credential }) => {
    if (!credential) {
      toast("Google sign-in did not complete. Please try again.", {
        type: "error",
        theme,
      });
      return;
    }

    axios
      .post(
        `${process.env.REACT_APP_API_BASE}/api/users/register/google`,
        { credential },
        { withCredentials: true }
      )
      .then((res) => {
        setJwtToken(res.data.token);
        setUserId(res.data.id);
        navigate("/dashboard");
        toast("Registered successfully.", { type: "success", theme });
      })
      .catch((err) => {
        toast(
          err.response?.data?.error ||
            "A user with that username already exists. Please log in.",
          { type: "error", theme }
        );
      });
  };

  const failureGoogle = () =>
    toast("Google sign-in failed. Please try again.", {
      type: "error",
      theme,
    });

  const onCancel = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setUsername("");
    setPassword("");
    setPasswordAgain("");
    setWebsite("");
    resetCaptcha();

    navigate("/");
  };

  return (
    <div className={styles.registerContainer}>
      <div className={styles.register}>
        <h1>Register</h1>
        <h3>
          By registering for an account, you automatically agree to the&nbsp;
          <Link
            style={{ color: themeObj.accentColor }}
            to="/terms-and-conditions"
          >
            Terms and Conditions
          </Link>{" "}
          and&nbsp;
          <Link style={{ color: themeObj.accentColor }} to="/privacy-policy">
            Privacy Policy
          </Link>
          .
        </h3>
        <GoogleLogin
          onSuccess={responseGoogle}
          onError={failureGoogle}
          text="continue_with"
          theme={theme === "dark" ? "filled_black" : "outline"}
        />
        <h3 style={{ color: themeObj.accentColor }}>
          <Link to="/account/login" style={{ color: themeObj.accentColor }}>
            Already have an account? Log in instead.
          </Link>
        </h3>
        <form
          className={styles.registerForm}
          onSubmit={onSubmit}
          onKeyDown={onKeyDown}
        >
          <label htmlFor="firstName">First name*</label>
          <TextField
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            id="firstName"
            variant="filled"
            size="small"
            className={styles.textField}
            error={!!firstNameError}
            helperText={firstNameError}
            InputProps={filledInputProps}
          />

          <label htmlFor="lastName">Last name*</label>
          <TextField
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            id="lastName"
            variant="filled"
            size="small"
            className={styles.textField}
            error={!!lastNameError}
            helperText={lastNameError}
            InputProps={filledInputProps}
          />

          <label htmlFor="email">Email address* (must be verified)</label>
          <TextField
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            id="email"
            variant="filled"
            size="small"
            className={styles.textField}
            error={!!emailError}
            helperText={emailError}
            InputProps={filledInputProps}
          />
          {emailError && emailError === "This email is already in use." && (
            <div className={styles.errorText}>
              {
                <span>
                  Would you like to <Link to="/account/login">log in</Link>{" "}
                  instead?
                </span>
              }
            </div>
          )}

          <label htmlFor="username">Username*</label>
          <TextField
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            id="username"
            variant="filled"
            size="small"
            className={styles.textField}
            error={!!usernameError}
            helperText={usernameError}
            InputProps={filledInputProps}
          />

          <div className={styles.passwordAndTooltip}>
            <label htmlFor="password">Password*</label>
            <div
              style={{
                color: themeObj.bodyColor,
                backgroundColor: themeObj.color,
              }}
              className={styles.questionMarkTooltip}
              data-tooltip-id={PASSWORD_REQUIREMENTS_TOOLTIP_ID}
              data-tooltip-html={PASSWORD_REQUIREMENTS_TOOLTIP_HTML}
            >
              ?
            </div>
          </div>
          <div className={styles.passwordAndEyeIcon}>
            <TextField
              type={isPasswordShowing ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              id="password"
              variant="filled"
              size="small"
              className={styles.textField}
              error={!!passwordError}
              helperText={passwordError}
              InputProps={filledInputProps}
            />
            <FontAwesomeIcon
              color={theme === "dark" ? "#ffffff" : "#000000"}
              icon={isPasswordShowing ? ["fas", "eye"] : ["fas", "eye-slash"]}
              onClick={() => setIsPasswordShowing(!isPasswordShowing)}
              className={styles.eyeIcon}
            />
          </div>

          <div className={styles.passwordAndTooltip}>
            <label htmlFor="passwordAgain">Re-type password*</label>
            <div
              style={{
                color: themeObj.bodyColor,
                backgroundColor: themeObj.color,
              }}
              className={styles.questionMarkTooltip}
              data-tooltip-id={PASSWORD_REQUIREMENTS_TOOLTIP_ID}
              data-tooltip-html={PASSWORD_REQUIREMENTS_TOOLTIP_HTML}
            >
              ?
            </div>
          </div>
          <div className={styles.passwordAndEyeIcon}>
            <TextField
              type={isPasswordAgainShowing ? "text" : "password"}
              value={passwordAgain}
              onChange={(e) => setPasswordAgain(e.target.value)}
              id="passwordAgain"
              variant="filled"
              size="small"
              className={styles.textField}
              error={!!passwordAgainError}
              helperText={passwordAgainError}
              InputProps={filledInputProps}
            />
            <FontAwesomeIcon
              color={theme === "dark" ? "#ffffff" : "#000000"}
              icon={
                isPasswordAgainShowing ? ["fas", "eye"] : ["fas", "eye-slash"]
              }
              onClick={() => setIsPasswordAgainShowing(!isPasswordAgainShowing)}
              className={styles.eyeIcon}
            />
          </div>

          <div className={styles.honeypot} aria-hidden="true">
            <label htmlFor="register-website">Leave this field empty</label>
            <input
              id="register-website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>

          {RECAPTCHA_SITE_KEY ? (
            <div className={styles.captchaRow}>
              <ReCaptcha
                ref={captchaRef}
                siteKey={RECAPTCHA_SITE_KEY}
                theme={theme === "dark" ? "dark" : "light"}
                onChange={setCaptchaToken}
              />
            </div>
          ) : null}

          <div>*required field</div>
          <div className={styles.registerFormButtons}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              className={styles.submitButton}
              type="submit"
              disabled={
                firstNameError ||
                lastNameError ||
                emailError ||
                usernameError ||
                passwordError ||
                passwordAgainError
              }
            >
              Submit
            </button>
          </div>
        </form>
        <Tooltip id={PASSWORD_REQUIREMENTS_TOOLTIP_ID} place="right" />
      </div>
    </div>
  );
};

export default Register;
